
import React from "react";
import type { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { PERMISSIONS } from "@/shared/constants/permissions";
import type { AppLayoutProps } from "./types";

// Lazy load tools features
const ExportFiles = React.lazy(() => import("@/app/pages/System/ExportFiles"));
const ExportFileDetails = React.lazy(() => import("@/app/pages/System/ExportFileDetails"));
const TripExportProfilePage = React.lazy(
  () => import("@/features/tools/pages/TripExportProfile/TripExportProfilePage")
);
const ConversionRulesPage = React.lazy(
  () => import("@/features/tools/pages/TripExportProfile/ConversionRulesPage")
);
const FieldsPage = React.lazy(
  () => import("@/features/tools/pages/TripExportProfile/FieldsPage")
);
const TripExportProfileDetails = React.lazy(
  () => import("@/features/tools/pages/TripExportProfile/TripExportProfileDetails")
);
const TripExportPage = React.lazy(
  () => import("@/features/tools/pages/TripExport/TripExportPage")
);
const ArchiveTripExportPage = React.lazy(
  () => import("@/features/tools/pages/ArchiveTripExport/ArchiveTripExportPage_New")
);
const FeedbackPage = React.lazy(() => import("@/features/tools/pages/Feedback/FeedbackPage"));
const FeedbackDetailsPage = React.lazy(
  () => import("@/features/tools/pages/Feedback/FeedbackDetailsPage")
);
const RoadCallsPage = React.lazy(() => import("@/features/tools/pages/RoadCalls/RoadCallsPage"));
const RoadCallsDetailsPage = React.lazy(
  () => import("@/features/tools/pages/RoadCalls/RoadCallsDetailsPage")
);
const DriverIncidentsPage = React.lazy(
  () => import("@/features/tools/pages/DriverIncidents/DriverIncidentsPage")
);
const DriverIncidentsDetailsPage = React.lazy(
  () => import("@/features/tools/pages/DriverIncidents/DriverIncidentsDetailsPage")
);
const DriverIncidentsHistoryPage = React.lazy(
  () => import("@/features/tools/pages/DriverIncidents/DriverIncidentsHistoryPage")
);
const VehicleMaintenancePage = React.lazy(
  () => import("@/features/tools/pages/VehicleMaintenance/VehicleMaintenance")
);
const DriverAdditionalInfoPage = React.lazy(
  () => import("@/features/tools/pages/DriverAdditionalInfo/DriverAdditionalInfoPage")
);
const RiderPolicyViolationsPage = React.lazy(() => import('@/features/tools/components/riderPolicyViolations/RiderPolicyViolationsPage'))
const RiderPolicyViolationDetailsPage = React.lazy(
  () => import('@/features/tools/pages/RiderPolicyViolations/RiderPolicyViolationDetailsPage')
);
const RiderPolicyPotentialViolationsPage = React.lazy(() => import('@/features/tools/components/riderPolicyPotentialViolations/RiderPolicyPotentialViolationsPage'))

export const toolsRoutes = (AppLayout: React.FC<AppLayoutProps>): RouteObject[] => [
  {
    path: "/tools/export-files",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.EXPORT_FILE_VIEW}>
          <ExportFiles />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/export-files/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.EXPORT_FILE_VIEW}>
          <ExportFileDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/trip-export-profiles",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW}>
          <TripExportProfilePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/trip-export-profiles/conversion-rules",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW}>
          <ConversionRulesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/trip-export-profiles/fields",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW}>
          <FieldsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/trip-export-profiles/:profileId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW}>
          <TripExportProfileDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/trip-export",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_OPERATION}>
          <TripExportPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/archive-trip-export",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_EXPORT_OPERATION}>
          <ArchiveTripExportPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/feedback",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FEEDBACK_ISSUE_VIEW}>
          <FeedbackPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/feedback/:feedbackId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FEEDBACK_ISSUE_VIEW}>
          <FeedbackDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/roadcalls",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ROADCALL_VIEW}>
          <RoadCallsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/roadcalls/new",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ROADCALL_CREATE}>
          <RoadCallsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/roadcalls/:roadCallId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ROADCALL_VIEW}>
          <RoadCallsDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/driver-incidents",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_INCIDENT_VIEW}>
          <DriverIncidentsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/driver-incidents/:driverIncidentId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_INCIDENT_VIEW}>
          <DriverIncidentsDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/driver-incidents/:driverIncidentId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_INCIDENT_UPDATE}>
          <DriverIncidentsHistoryPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/vehicle-maintenance",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.VEHICLE_ADDON_VIEW}>
          <VehicleMaintenancePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/tools/driver-additional-info",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_ADDON_VIEW}>
          <DriverAdditionalInfoPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: '/tools/rider-policy-violations',
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RIDER_POLICY_VIOLATION_VIEW}>
          <RiderPolicyViolationsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: '/tools/rider-policy-violations/:violationId',
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RIDER_POLICY_VIOLATION_VIEW}>
          <RiderPolicyViolationDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: '/tools/rider-policy-potential-violations',
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RIDER_POLICY_VIOLATION_VIEW}>
          <RiderPolicyPotentialViolationsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
];
